import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { getGridTotalStatsKeys } from '../../service/GridHelpers/GridHelpers';
import { CurrencyCode } from '../../typings/graphql-grid';
import { GridTotalStatsInfo } from '../../typings/grid';
import { GridTotalStats } from '../GridTotalStats/GridTotalStats';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './GridTotalBox.css';
import * as keyset from './GridTotalBox.i18n';

const i18n = i18nFactory(keyset);

export interface GridTotalBoxProps {
    totalStats?: GridTotalStatsInfo;
    currency?: CurrencyCode;
}

export interface GridTotalBoxState {
    offsetStatsCount: number;
}

export class GridTotalBox extends React.PureComponent<GridTotalBoxProps, GridTotalBoxState> {
    private scrollContainer: React.ReactInstance | null;

    constructor(props: GridTotalBoxProps) {
        super(props);

        this.state = {
            offsetStatsCount: 0
        };
    }

    render() {
        const { totalStats } = this.props;

        return (
            <div className={Bem.cls('grid-screen-total-box')}>
                <div className={Bem.cls('grid-screen-total-box', 'items-wrapper')}>
                    <div
                        ref={ref => (this.scrollContainer = ref)}
                        className={Bem.cls('grid-screen-total-box', 'items')}
                        style={{
                            transform: `translateX(-${this.calculateItemsWrapperOffset()}px)`
                        }}
                    >
                        {totalStats ? this.getTotalStats() : this.getSkeletonContent()}
                    </div>
                </div>
                {this.getPrevButton()}
                {this.getNextButton()}
            </div>
        );
    }

    private getSkeletonContent() {
        return <span className={Bem.cls('grid-screen-total-box', 'skeleton')} />;
    }

    private getTotalStats() {
        const { totalStats, currency } = this.props;

        if (!totalStats) {
            return;
        }

        return getGridTotalStatsKeys().map((key: keyof GridTotalStatsInfo, index) => {
            const stats = {
                title: i18n(key),
                description: totalStats[key],
                type: key,
                difference: undefined,
                currency
            };

            return <GridTotalStats {...stats} key={index} />;
        });
    }

    private getPrevButton() {
        if (this.state.offsetStatsCount) {
            return (
                <div
                    className={Bem.cls('grid-screen-total-box', 'btn', {
                        type: 'prev'
                    })}
                    onClick={() => {
                        this.setState({
                            offsetStatsCount: this.state.offsetStatsCount - 1
                        });
                    }}
                >
                    <Shape2
                        width={16}
                        height={16}
                        icon={Shape2Icon.verticalArrow}
                        cls={Bem.cls('grid-screen-total-box', 'icon', {
                            rotate: 'left'
                        })}
                    />
                </div>
            );
        }

        return;
    }

    private getNextButton() {
        if (this.scrollContainer) {
            const { clientWidth } = this.scrollContainer as Element;
            const offset = this.calculateItemsWrapperOffset();
            const childrenWidth = this.calculateChildrenWidth();

            if (childrenWidth - offset > clientWidth) {
                return (
                    <div
                        className={Bem.cls('grid-screen-total-box', 'btn', {
                            type: 'next'
                        })}
                        onClick={() => {
                            this.setState({
                                offsetStatsCount: this.state.offsetStatsCount + 1
                            });
                        }}
                    >
                        <Shape2
                            width={16}
                            height={16}
                            icon={Shape2Icon.verticalArrow}
                            cls={Bem.cls('grid-screen-total-box', 'icon', {
                                rotate: 'right'
                            })}
                        />
                    </div>
                );
            }
        }

        return;
    }

    private calculateItemsWrapperOffset() {
        if (this.scrollContainer) {
            const { childNodes } = this.scrollContainer as Element;

            return Array.from(childNodes)
                .slice(0, this.state.offsetStatsCount)
                .reduce((acc: number, item: Element) => {
                    return acc + item.clientWidth;
                }, 0);
        }

        return 0;
    }

    private calculateChildrenWidth() {
        if (this.scrollContainer) {
            const { childNodes } = this.scrollContainer as Element;
            return Array.from(childNodes).reduce((acc: number, item: Element) => {
                return acc + item.clientWidth;
            }, 0);
        }

        return 0;
    }
}
